import { useState, useEffect, useRef } from 'react'
import { Button } from '../../ui/button'
import { Checkbox } from '../../ui/checkbox'
import { Label } from '../../ui/label'
import { ArrowLeft, ArrowRight, AlertCircle } from 'lucide-react'
import type { DealFormData } from '../../../lib/types'
import {
  CURATOR_PUBLIC_SALE_AGREEMENT,
  CURATOR_PRIVATE_SALE_AGREEMENT,
  MARKETING_GUIDELINES_PRIVATE_SALE,
  CUSTOM_SALE_LEGAL_AGREEMENT,
} from '../../../lib/legal-texts'

interface StepLegalAgreementsProps {
  formData: DealFormData
  onComplete: (data: Partial<DealFormData>) => void
  onUpdate: (data: Partial<DealFormData>) => void
  onBack: () => void
  headingId?: string
  onValidationError?: (errorMessage: string) => void
  onValidationErrors?: (errors: { id: string; label: string }[]) => void
  onSubmitDeal?: () => void
  isSubmitting?: boolean
  requestChanges?: Record<string, string>
}

export function StepLegalAgreements({
  formData,
  onComplete,
  onUpdate,
  onBack,
  headingId,
  onValidationError,
  onValidationErrors,
  onSubmitDeal,
  isSubmitting,
  requestChanges,
}: StepLegalAgreementsProps) {
  const [localData, setLocalData] = useState({
    marketingGuidelinesAgreementAccepted:
      formData.marketingGuidelinesAgreementAccepted || false,
  })
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
  const [checkboxError, setCheckboxError] = useState('')
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const [currentStep, setCurrentStep] = useState(1)

  const isPrivateSale = (formData.salesType || '')
    .toLowerCase()
    .includes('private')
  const isPublicSale = (formData.salesType || '')
    .toLowerCase()
    .includes('public')

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    setHasScrolledToBottom(false)
    container.scrollTop = 0

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10
      setHasScrolledToBottom(isAtBottom)
    }
    container.addEventListener('scroll', handleScroll)
    handleScroll()

    return () => container.removeEventListener('scroll', handleScroll)
  }, [currentStep, isPrivateSale])

  const updateField = (field: string, value: any) => {
    const updatedData = { ...localData, [field]: value }
    setLocalData(updatedData)
    onUpdate(updatedData)
    if (field === 'marketingGuidelinesAgreementAccepted' && value === true) {
      setCheckboxError('')
    }
  }

  const handleSubmit = () => {
    if (isPrivateSale) {
      if (currentStep === 1) {
        setCurrentStep(2)
        return
      } else {
        if (canSubmit) {
          onValidationErrors?.([])
          onValidationError?.('')
          if (onSubmitDeal) onSubmitDeal()
          else onComplete(localData)
        } else {
          setCheckboxError(
            'You must accept the Marketing Guidelines to continue.',
          )
          const mappedErrors = [
            { id: 'legal-agreements', label: 'Marketing Guidelines' },
          ]
          if (onValidationErrors) {
            onValidationErrors(mappedErrors)
          } else {
            onValidationError?.(
              'Please complete all required fields before continuing.',
            )
          }
        }
      }
    } else {
      const finalData = {
        ...localData,
        marketingGuidelinesAgreementAccepted: true,
      }
      onValidationErrors?.([])
      onValidationError?.('')
      if (onSubmitDeal) onSubmitDeal()
      else onComplete(finalData)
    }
  }

  const handleBack = () => {
    if (isPrivateSale && currentStep === 2) {
      setCurrentStep(1)
    } else {
      onBack()
    }
  }

  const canSubmit = isPrivateSale
    ? currentStep === 2 && localData.marketingGuidelinesAgreementAccepted
    : true

  const getAgreementName = () => {
    if (isPrivateSale) {
      if (currentStep === 1) return 'Curator Private Sale Agreement'
      return 'Marketing Guidelines (Private Sales)'
    }
    if (isPublicSale) {
      return 'Curator Sale Agreement'
    }
    return 'Legal Agreements for This Sale Type'
  }

  const getStepDescription = () => {
    if (isPrivateSale) {
      return `Step ${currentStep} of 2`
    }
    return null
  }

  const getAgreementContent = () => {
    if (isPrivateSale) {
      if (currentStep === 1) return CURATOR_PRIVATE_SALE_AGREEMENT
      return MARKETING_GUIDELINES_PRIVATE_SALE
    }
    if (isPublicSale) {
      return CURATOR_PUBLIC_SALE_AGREEMENT
    }
    return CUSTOM_SALE_LEGAL_AGREEMENT
  }

  const getButtonText = () => {
    if (isPrivateSale && currentStep === 1) {
      return 'Continue to Marketing Guidelines'
    }
    return 'Continue'
  }

  const showCheckbox = isPrivateSale && currentStep === 2

  return (
    <div className="space-y-8">
      <div className="space-y-6">
        {isPrivateSale && (
          <p className="text-neutral-600 text-base mt-4 mb-2">
            {getStepDescription()}
          </p>
        )}

        <h3 className="text-lg font-medium text-neutral-900">
          {getAgreementName()}
        </h3>

        <div className="border border-neutral-200 rounded-lg overflow-hidden">
          <div
            ref={scrollContainerRef}
            className="p-6 overflow-y-auto bg-white"
            style={{ maxHeight: '60vh' }}
          >
            <div
              className="prose prose-sm max-w-none text-neutral-700 leading-relaxed [&_p]:mb-4 [&_ol]:mb-4 [&_ol]:pl-5 [&_ol]:list-decimal [&_li]:mb-2"
              dangerouslySetInnerHTML={{ __html: getAgreementContent() }}
            />
          </div>
        </div>

        {showCheckbox && (
          <div
            id="legal-agreements"
            className="flex items-center space-x-3 p-4 bg-neutral-50 rounded-lg border border-neutral-200"
          >
            <Checkbox
              id="legal-agreements-checkbox"
              checked={localData.marketingGuidelinesAgreementAccepted}
              onCheckedChange={(checked) =>
                updateField(
                  'marketingGuidelinesAgreementAccepted',
                  checked === true,
                )
              }
              className="border-2 border-brand-primary rounded-sm text-brand-primary focus:ring-brand-primary accent-brand-primary"
              disabled={!hasScrolledToBottom}
            />
            <Label
              htmlFor="legal-agreements-checkbox"
              className={`text-sm font-medium cursor-pointer leading-relaxed ${
                hasScrolledToBottom ? 'text-neutral-900' : 'text-neutral-400'
              }`}
            >
              I have read and agree to the Marketing Guidelines for Private
              Sales.
            </Label>
          </div>
        )}

        <div className="flex flex-col gap-1">
          {checkboxError && showCheckbox && (
            <p className="text-xs text-status-error inline-flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {checkboxError}
            </p>
          )}
          {requestChanges?.marketingGuidelinesAgreementAccepted &&
            showCheckbox && (
              <p className="text-xs text-status-error inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {requestChanges.marketingGuidelinesAgreementAccepted}
              </p>
            )}
        </div>
      </div>
      <div className="flex justify-between pt-6 border-t border-neutral-200 border-none">
        <Button
          type="button"
          variant="outline"
          onClick={handleBack}
          size="md"
          className="flex items-center gap-2 bg-transparent px-5"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          variant="brand"
          size="md"
          className="flex items-center gap-2 px-5 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={
            !!isSubmitting ||
            (isPrivateSale && currentStep === 1 ? false : !canSubmit)
          }
        >
          {getButtonText()}
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  )
}
